import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { redirect } from 'next/navigation';

import { useStepNavigation } from '@hooks/useStepNavigation';
import { useSupabaseAuth } from '@hooks/useSupabaseAuth';
import { useTeacherProfile } from '@hooks/useTeacherProfile';
import { TeacherProfile, UserStatus } from 'types/collections';
import { ROLES } from 'types/roles';

import { DailyAvailability, DAYS } from '@components/DaySelectionPanel';
import { FoundAddress } from '@components/Onboarding/TeacherOnboarding/types';
import { showToast } from '@components/ToastContainer';

import {
  fetchAddressFromAPI,
  formatPhoneForSaving,
  toggleDayInAvailability,
  validatePhone,
} from '@utils/helpers';
import {
  getUserImageData,
  uploadUserImage,
  validateImageFile,
} from '@utils/helpers/imageUploadHelpers';

interface TeacherOnboardingData {
  selectedDays: DailyAvailability;
  firstName: string;
  lastName: string;
  phone: string;
  postCodeInput: string;
  houseNumberInput: string;
  address: FoundAddress | null;
}

interface OnboardingErrors {
  firstName?: boolean;
  lastName?: boolean;
  phone?: boolean;
  address?: boolean;
}

const initialDaysState = () => {
  return DAYS.reduce((acc, day) => {
    acc[day] = true;
    return acc;
  }, {} as DailyAvailability);
};

const STEPS = ['introduction', 'uploadPhoto', 'name', 'phone', 'address'];

export const useTeacherOnboardingLogic = () => {
  const { t } = useTranslation('common');
  const { updateTeacherProfile, teacher, isPending: isProfileLoading } = useTeacherProfile();
  const { user } = useSupabaseAuth();
  const [data, setData] = useState<TeacherOnboardingData>({
    selectedDays: initialDaysState(),
    firstName: '',
    lastName: '',
    phone: '',
    postCodeInput: '',
    houseNumberInput: '',
    address: null,
  });
  const [errors, setErrors] = useState<OnboardingErrors>({});
  const { currentStep, setCurrentStep, handleBackStep, handleNext } = useStepNavigation(
    STEPS.length,
  );
  const latestRequestRef = useRef<number>(0);

  // Scroll to top on step change and keyboard dismiss
  useEffect(() => {
    const scrollResetTimeout = setTimeout(() => {
      document.body.getBoundingClientRect();

      requestAnimationFrame(() => {
        window.scrollTo(0, 0);
      });
    }, 50);

    const handleFocusOut = () => {
      setTimeout(() => {
        window.scrollTo(0, window.scrollY + 1);
        window.scrollTo(0, window.scrollY - 1);
      }, 100);
    };

    window.addEventListener('focusout', handleFocusOut);

    return () => {
      clearTimeout(scrollResetTimeout);
      window.removeEventListener('focusout', handleFocusOut);
    };
  }, [currentStep]);

  useEffect(() => {
    if (teacher && !isProfileLoading) {
      // Determine the correct step based on the profile data
      const initialStep = determineStepFromProfile(teacher);

      setCurrentStep(initialStep);

      // You can also populate your form state here
      setData((prevData) => ({
        ...prevData,
        firstName: teacher.first_name || '',
        lastName: teacher.second_name || '',
        phone: (teacher.phone_number || '').replace(/^\+31/, ''),
        postCodeInput: teacher.postal_code || '',
        houseNumberInput: teacher.house_number || '',
        selectedDays: {
          ...prevData.selectedDays,
          ...DAYS.reduce((acc, day) => {
            const key = `general_availability_${day}` as keyof TeacherProfile;
            if (typeof teacher[key] === 'boolean') {
              acc[day] = teacher[key] as boolean;
            }
            return acc;
          }, {} as DailyAvailability),
        },
      }));
    }
  }, [teacher, isProfileLoading, setCurrentStep]);

  // Simple step determination
  const determineStepFromProfile = (profile: TeacherProfile): number => {
    if (!profile) return STEPS.indexOf('introduction');

    // STEP 0: Introduction (only shown on first visit when profile is completely empty)
    const isCompletelyNew = !profile.avatar_url && 
                           !profile.first_name && 
                           !profile.second_name && 
                           !profile.phone_number;
    
    if (isCompletelyNew) {
      return STEPS.indexOf('introduction');
    }

    // STEP 1: Photo Upload (Check for avatar_url)
    if (!profile.avatar_url) {
      return STEPS.indexOf('uploadPhoto');
    }

    // STEP 2: Name (Check for first and last name)
    if (!profile.first_name || !profile.second_name) {
      return STEPS.indexOf('name');
    }

    // STEP 3: Phone (Check for phone number)
    if (!profile.phone_number) {
      return STEPS.indexOf('phone');
    }

    // STEP 4: Address (Check for address details)
    const hasAddress =
      profile.address && profile.house_number && profile.postal_code && profile.city;
    if (!hasAddress) {
      return STEPS.indexOf('address');
    }

    // If address is present and status is approved, they are done.
    if (profile.status === UserStatus.APPROVED) {
      // TODO: Redirect to the shifts
      // For now, it will stay on the last step of the form.
      return STEPS.length - 1;
    }

    // Default to the last step if all data is present but not yet 'approved'.
    return STEPS.indexOf('address');
  };

  // useEffect to fetch the address from the API with debounce delay
  useEffect(() => {
    const shouldFetch = data.postCodeInput?.length >= 6 && data.houseNumberInput?.length > 0;

    if (shouldFetch) {
      // Add a 1.5 sec delay to prevent validation errors while user is still typing
      const timeoutId = setTimeout(() => {
        const currentRequest = ++latestRequestRef.current;
        setErrors((prev) => ({ ...prev, address: false }));

        const findAddress = async () => {
          try {
            const foundAddress = await fetchAddressFromAPI(
              data.postCodeInput,
              data.houseNumberInput,
            );
            if (currentRequest === latestRequestRef.current) {
              setData((prev) => ({ ...prev, address: foundAddress }));
            }
          } catch (error) {
            if (currentRequest === latestRequestRef.current) {
              setData((prev) => ({ ...prev, address: null }));
              setErrors((prev) => ({ ...prev, address: true }));
              const toastKey =
                error instanceof Error && error.message.includes('API')
                  ? 'addressLookupError'
                  : 'addressNotFound';
              showToast('error', t(`toast.${toastKey}`), t(`toast.${toastKey}Description`));
            }
          }
        };
        findAddress();
      }, 1500);

      // Cleanup timeout if component unmounts or dependencies change
      return () => clearTimeout(timeoutId);
    } else {
      setData((prev) => ({ ...prev, address: null }));
      setErrors((prev) => ({ ...prev, address: false }));

      latestRequestRef.current++;
    }
  }, [data.postCodeInput, data.houseNumberInput, t]);

  const selectAndUploadPhoto = useCallback(async () => {
    if (!teacher?.id) {
      console.error('Teacher profile not available for photo upload.');
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';

    input.onchange = async (event) => {
      const target = event.target as HTMLInputElement;
      const file = target.files?.[0];

      if (file) {
        try {
          const validation = validateImageFile(file);
          if (!validation.valid) {
            showToast('error', t('toast.errorPhotoUpload'), t('toast.errorPhotoUploadSize'));
            return;
          }
          // Get current user data to check existing image
          if (!user) {
            console.error('User not available for photo upload.');
            return;
          }
          const userData = await getUserImageData(user);
          const result = await uploadUserImage(file, user, ROLES.TEACHER, userData.currentPath);

          if (!result.success) {
            throw new Error(result.error);
          }
          // TODO: Add success toast ?
          handleNext();
        } catch (error) {
          console.error('Error uploading photo:', error);
          showToast('error', t('toast.errorPhotoUpload'), t('toast.errorPhotoUploadDescription'));
        }
      }
    };

    input.click();
  }, [teacher, handleNext, t, user]);

  const handleNextStep = useCallback(async () => {
    const currentStepName = STEPS[currentStep];
    let isValid = true;
    let newErrors: OnboardingErrors = {};

    // Step-specific validation
    if (currentStepName === 'introduction') {
      // Introduction step has no validation, just proceed to next step
      handleNext();
      return;
    } else if (currentStepName === 'uploadPhoto') {
      await selectAndUploadPhoto();
      return;
    } else if (currentStepName === 'name') {
      if (!data.firstName.trim()) {
        newErrors.firstName = true;
        isValid = false;
      }
      if (!data.lastName.trim()) {
        newErrors.lastName = true;
        isValid = false;
      }

      setErrors(newErrors);
      if (!isValid) {
        showToast('error', t('toast.errorGeneric'), t('toast.errorGenericDescription'));
        return;
      }

      try {
        await updateTeacherProfile({
          first_name: data.firstName,
          second_name: data.lastName,
        });
        handleNext();
      } catch (error) {
        console.error('Error updating teacher profile:', error);
        showToast('error', t('toast.errorGeneric'), t('toast.errorGenericDescription'));
      }
    } else if (currentStepName === 'phone') {
      isValid = validatePhone(data.phone);
      if (!isValid) {
        newErrors.phone = true;
        setErrors(newErrors);
        showToast('error', t('toast.errorPhone'), t('toast.errorPhoneDescription'));
        return;
      }
      try {
        await updateTeacherProfile({
          phone_number: formatPhoneForSaving(data.phone),
        });
        handleNext();
      } catch (error: any) {
        console.error('Error updating teacher profile phone:', error);

        // Check for specific error types
        if (error?.status === 409) {
          newErrors.phone = true;
          setErrors(newErrors);
          showToast(
            'error',
            t('toast.errorPhoneDuplicate'),
            t('toast.errorPhoneDuplicateDescription'),
          );
        } else {
          showToast('error', t('toast.errorGeneric'), t('toast.errorGenericDescription'));
        }
      }
    } else if (currentStepName === 'address') {
      if (!data.address) {
        setErrors({ address: true });
        showToast('error', t('toast.addressNotFound'), t('toast.addressNotFoundDescription'));
        return;
      }
      try {
        await updateTeacherProfile({
          address: data.address.fullAddress,
          house_number: data.address.houseNumber,
          street: data.address.street,
          city: data.address.city,
          postal_code: data.address.postCode,
          latitude: data.address.coordinates.latitude,
          longitude: data.address.coordinates.longitude,
          status: UserStatus.APPROVED,
        });
      } catch (error) {
        console.error('Error updating teacher profile address:', error);
        showToast('error', t('toast.errorGeneric'), t('toast.errorGenericDescription'));
      }
      redirect(`/${ROLES.TEACHER}/overview`);
    }
  }, [currentStep, data, t, updateTeacherProfile, handleNext, selectAndUploadPhoto]);

  const handleToggleDay = (day: string) => {
    setData((prev) => ({
      ...prev,
      selectedDays: toggleDayInAvailability(prev.selectedDays, day),
    }));
  };

  const setFirstName = (firstName: string) => {
    setData((prev) => ({ ...prev, firstName }));
    if (errors.firstName) {
      setErrors((prev) => ({ ...prev, firstName: false }));
    }
  };

  const setLastName = (lastName: string) => {
    setData((prev) => ({ ...prev, lastName }));
    if (errors.lastName) {
      setErrors((prev) => ({ ...prev, lastName: false }));
    }
  };

  const setPhone = (phone: string) => {
    setData((prev) => ({ ...prev, phone }));
    if (errors.phone) {
      setErrors((prev) => ({ ...prev, phone: false }));
    }
  };

  const setPostCode = (postCode: string) => {
    setData((prev) => ({ ...prev, postCodeInput: postCode.replace(/\s+/g, '') }));
  };

  const setHouseNumber = (houseNumber: string) => {
    setData((prev) => ({ ...prev, houseNumberInput: houseNumber }));
  };

  return {
    currentStep,
    steps: STEPS,
    data,
    errors,
    isLoading: isProfileLoading,

    handleBackStep,
    handleNextStep,
    handleToggleDay,
    setFirstName,
    setLastName,
    setPhone,
    setPostCode,
    setHouseNumber,
  };
};
